/**
 * wa-select — form-associated custom element replacing <select>
 *
 * Usage:
 *   <script type="module" src="wa-select.js"></script>
 *   <wa-select name="plan" placeholder="Choose a plan…">
 *     <optgroup label="Individual">
 *       <option value="free">Free</option>
 *       <option value="pro">Pro — $12/mo</option>
 *     </optgroup>
 *     <option value="other">Other</option>
 *   </wa-select>
 *
 * Attributes:  name, value, placeholder, disabled, required
 * Properties:  .value (read/write), .disabled (read/write), .required (read/write),
 *              .name, .placeholder
 * Events:      change (bubbles, composed — fires on user selection)
 *
 * Theming — set any of these CSS custom properties on the host element:
 *   --wa-font               --wa-font-size
 *   --wa-min-width          --wa-padding          --wa-radius
 *   --wa-bg                 --wa-color            --wa-border
 *   --wa-border-hover       --wa-border-focus     --wa-arrow-color
 *   --wa-placeholder-color
 *   --wa-menu-bg            --wa-menu-border      --wa-menu-color
 *   --wa-option-hover
 *   --wa-option-selected-bg --wa-option-selected-color
 *   --wa-group-color        --wa-group-divider
 *
 * Parts (for ::part() styling):
 *   trigger, label, listbox, option
 *   trigger state parts: trigger-focused (present while the trigger has visible
 *                        keyboard focus or the menu is open)
 *   option state parts:  option-selected, option-highlighted, option-disabled
 *   (e.g. wa-select::part(option):hover  or  wa-select::part(trigger-focused))
 */

class WaSelect extends HTMLElement {
  static formAssociated = true;

  /** @type {ElementInternals} */ #internals;
  /** @type {ShadowRoot} */       #shadow;
  /** @type {HTMLButtonElement} */ #trigger;
  /** @type {HTMLUListElement} */  #listbox;
  /** @type {HTMLSpanElement} */   #label;
  /** @type {HTMLSpanElement} */   #labelRef; // receives external <label for> text

  /** @type {Array<{value:string, text:string, disabled:boolean}>} */
  #items = [];

  #value        = '';
  #defaultValue = ''; // saved at connect time for formResetCallback
  #highlighted  = -1;
  #mo           = null;

  // Type-ahead state
  #typeBuffer = '';
  #typeTimer  = null;

  // Stable UID for aria-controls / option IDs inside this shadow root
  #uid = `wa-${Math.random().toString(36).slice(2, 8)}`;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  constructor() {
    super();
    this.#internals = this.attachInternals();
    this.#shadow    = this.attachShadow({ mode: 'open' });
    this.#shadow.innerHTML = this.#buildTemplate();

    this.#trigger  = this.#shadow.querySelector('.trigger');
    this.#listbox  = this.#shadow.querySelector('.listbox');
    this.#label    = this.#shadow.querySelector('.label');
    this.#labelRef = this.#shadow.querySelector('.label-ref');

    // stopPropagation prevents the host click listener below from double-firing
    this.#trigger.addEventListener('click', (e) => { e.stopPropagation(); this.#toggle(); });
    this.#trigger.addEventListener('keydown', (e) => this.#onTriggerKey(e));
    this.#trigger.addEventListener('focus', () => this.#syncFocusPart());
    this.#trigger.addEventListener('blur',  () => this.#syncFocusPart());

    // Handle <label for="id"> activation — label clicks fire directly on the host,
    // not through the shadow DOM trigger, so this listener catches them
    this.addEventListener('click', (e) => {
      if (!e.composedPath().includes(this.#trigger)) this.#toggle();
    });
  }

  static get observedAttributes() {
    return ['disabled', 'value', 'placeholder', 'required'];
  }

  attributeChangedCallback(name, _old, val) {
    if (!this.#trigger) return;
    if (name === 'disabled')    this.#trigger.disabled = val !== null;
    if (name === 'value')       this.#applyValue(val ?? '', false);
    if (name === 'placeholder') this.#syncLabel();
    if (name === 'required')    this.#syncValidity();
  }

  connectedCallback() {
    // Save the initial HTML-declared value so formResetCallback can restore it
    this.#defaultValue = this.getAttribute('value') ?? '';

    this.#buildList();
    if (this.hasAttribute('value')) {
      this.#applyValue(this.getAttribute('value'), false);
    }

    // Propagate <label for="id"> text to the trigger's accessible name.
    // Deferred so the external label element has been parsed.
    requestAnimationFrame(() => this.#syncTriggerLabel());

    this.#mo = new MutationObserver(() => {
      this.#buildList();
      this.#syncLabel();
    });
    this.#mo.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#mo?.disconnect();
    this.#closeMenu();
  }

  // Restores state when the parent <form> is reset
  formResetCallback() {
    this.#applyValue(this.#defaultValue, false);
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  get value()     { return this.#value; }
  set value(v)    { this.#applyValue(String(v), true); }

  get disabled()  { return this.hasAttribute('disabled'); }
  set disabled(v) { this.toggleAttribute('disabled', Boolean(v)); }

  get required()  { return this.hasAttribute('required'); }
  set required(v) { this.toggleAttribute('required', Boolean(v)); }

  get name()        { return this.getAttribute('name')        ?? ''; }
  get placeholder() { return this.getAttribute('placeholder') ?? 'Select…'; }

  // ── List building ──────────────────────────────────────────────────────────

  #buildList() {
    this.#items = [];
    this.#listbox.innerHTML = '';

    for (const child of this.children) {
      if (child.tagName === 'OPTGROUP') {
        this.#appendGroup(child);
      } else if (child.tagName === 'OPTION') {
        this.#appendOption(child, false);
      }
    }

    this.#syncSelected();
  }

  #appendGroup(group) {
    const header = document.createElement('li');
    header.className = 'group-label';
    header.setAttribute('role', 'presentation');
    header.setAttribute('aria-hidden', 'true');
    header.textContent = group.getAttribute('label') ?? '';
    this.#listbox.appendChild(header);

    for (const opt of group.querySelectorAll(':scope > option')) {
      this.#appendOption(opt, group.disabled);
    }
  }

  #appendOption(opt, groupDisabled) {
    const i    = this.#items.length;
    const item = {
      value:    opt.value,
      text:     opt.textContent.trim(),
      disabled: opt.disabled || groupDisabled,
    };
    this.#items.push(item);

    const li = document.createElement('li');
    li.className = 'option';
    li.setAttribute('role', 'option');
    li.setAttribute('id', `${this.#uid}-opt-${i}`);
    li.part.add('option');
    li.textContent = item.text;
    if (item.disabled) {
      li.setAttribute('aria-disabled', 'true');
      li.part.add('option-disabled');
    }

    // stopPropagation prevents the host click listener from re-toggling the menu open
    li.addEventListener('click',      (e) => { e.stopPropagation(); if (!item.disabled) this.#select(i); });
    li.addEventListener('mouseenter', ()  => { if (!item.disabled) this.#highlight(i); });
    this.#listbox.appendChild(li);
  }

  // ── State sync ─────────────────────────────────────────────────────────────

  #syncSelected() {
    this.#shadow.querySelectorAll('.option').forEach((li, i) => {
      const selected = this.#items[i]?.value === this.#value;
      if (selected) {
        li.setAttribute('aria-selected', 'true');
      } else {
        li.removeAttribute('aria-selected');
      }
      li.part.toggle('option-selected', selected);
    });
  }

  #syncLabel() {
    const item = this.#items.find(i => i.value === this.#value);
    if (item) {
      this.#label.textContent = item.text;
      this.#label.classList.remove('is-placeholder');
    } else {
      this.#label.textContent = this.placeholder;
      this.#label.classList.add('is-placeholder');
    }
  }

  // Read any associated <label for="id"> and copy its text into the hidden
  // .label-ref span so aria-labelledby announces "[field label] [value]"
  #syncTriggerLabel() {
    const labels = this.#internals.labels;
    this.#labelRef.textContent = labels.length
      ? [...labels].map(l => l.textContent.trim()).filter(Boolean).join(' ')
      : '';
  }

  // Mirrors the shadow CSS focus-ring condition (:focus-visible or open menu)
  // onto a "trigger-focused" part so document CSS can restyle the focus state
  #syncFocusPart() {
    if (!this.#trigger) return;
    this.#trigger.part.toggle(
      'trigger-focused',
      this.#trigger.matches(':focus-visible') || this.hasAttribute('open')
    );
  }

  #syncValidity() {
    if (this.required && !this.#value) {
      this.#internals.setValidity(
        { valueMissing: true },
        'Please select an option.',
        this.#trigger
      );
    } else {
      this.#internals.setValidity({});
    }
  }

  #applyValue(val, dispatch) {
    this.#value = val;
    this.#internals.setFormValue(val || null);
    this.#syncSelected();
    this.#syncLabel();
    this.#syncValidity();
    if (dispatch) {
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    }
  }

  // ── Menu open / close ──────────────────────────────────────────────────────

  #openMenu() {
    if (this.disabled) return;
    this.setAttribute('open', '');
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#syncFocusPart();
    const idx = this.#items.findIndex(i => i.value === this.#value);
    this.#highlight(idx >= 0 ? idx : 0);
    document.addEventListener('click',   this.#onDocClick, true);
    document.addEventListener('keydown', this.#onDocKey);
  }

  #closeMenu() {
    this.removeAttribute('open');
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#syncFocusPart();
    this.#highlight(-1);
    clearTimeout(this.#typeTimer);
    this.#typeBuffer = '';
    document.removeEventListener('click',   this.#onDocClick, true);
    document.removeEventListener('keydown', this.#onDocKey);
  }

  #toggle() {
    this.hasAttribute('open') ? this.#closeMenu() : this.#openMenu();
  }

  // ── Highlight & select ─────────────────────────────────────────────────────

  #highlight(idx) {
    const opts = this.#shadow.querySelectorAll('.option');
    opts.forEach((li, i) => {
      li.classList.toggle('highlighted', i === idx);
      li.part.toggle('option-highlighted', i === idx);
    });
    this.#highlighted = idx;

    if (idx >= 0 && opts[idx]) {
      opts[idx].scrollIntoView({ block: 'nearest' });
      this.#trigger.setAttribute('aria-activedescendant', opts[idx].id);
    } else {
      this.#trigger.removeAttribute('aria-activedescendant');
    }
  }

  #select(idx) {
    const item = this.#items[idx];
    if (!item || item.disabled) return;
    this.#applyValue(item.value, true);
    this.#closeMenu();
    this.#trigger.focus();
  }

  // ── Type-ahead ─────────────────────────────────────────────────────────────

  #typeahead(char) {
    clearTimeout(this.#typeTimer);
    this.#typeTimer = setTimeout(() => { this.#typeBuffer = ''; }, 500);

    this.#typeBuffer += char.toLowerCase();
    const buf   = this.#typeBuffer;
    const items = this.#items;
    const len   = items.length;

    const isCycling = buf.length > 1 && [...buf].every(c => c === buf[0]);
    const start     = isCycling ? (this.#highlighted + 1) % len : 0;

    for (let n = 0; n < len; n++) {
      const idx = (start + n) % len;
      if (!items[idx].disabled && items[idx].text.toLowerCase().startsWith(buf)) {
        this.#highlight(idx);
        return;
      }
    }

    if (buf.length > 1) {
      this.#typeBuffer = char.toLowerCase();
      for (let n = 0; n < len; n++) {
        const idx = (this.#highlighted + 1 + n) % len;
        if (!items[idx].disabled && items[idx].text.toLowerCase().startsWith(this.#typeBuffer)) {
          this.#highlight(idx);
          return;
        }
      }
    }
  }

  // ── Event handlers ─────────────────────────────────────────────────────────

  #onTriggerKey = (e) => {
    if (!this.hasAttribute('open') &&
        (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      this.#openMenu();
    }
  };

  #onDocClick = (e) => {
    if (!e.composedPath().includes(this)) this.#closeMenu();
  };

  #onDocKey = (e) => {
    const len = this.#items.length;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        this.#closeMenu();
        this.#trigger.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.#highlight(Math.min(len - 1, this.#highlighted + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.#highlight(Math.max(0, this.#highlighted - 1));
        break;
      case 'Home':
        e.preventDefault();
        this.#highlight(0);
        break;
      case 'End':
        e.preventDefault();
        this.#highlight(len - 1);
        break;
      case 'Enter':
      case ' ':
        if (this.#highlighted >= 0) {
          e.preventDefault();
          this.#select(this.#highlighted);
        }
        break;
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault();
          this.#typeahead(e.key);
        }
    }
  };

  // ── Shadow DOM ─────────────────────────────────────────────────────────────

  #buildTemplate() {
    const listboxId  = `${this.#uid}-listbox`;
    const labelRefId = `${this.#uid}-lref`;
    const labelId    = `${this.#uid}-label`;

    return `
<style>
  :host {
    display: inline-block;
    position: relative;
    font-family: var(--wa-font, system-ui, -apple-system, sans-serif);
    font-size: var(--wa-font-size, 0.9375rem);
    line-height: 1.5;
  }

  :host([disabled]) { opacity: 0.5; pointer-events: none; }

  /* ── Trigger ── */
  .trigger {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    min-width: var(--wa-min-width, 180px);
    /* min-height ensures 44px touch target — WCAG 2.5.8 */
    min-height: 44px;
    padding: var(--wa-padding, 0.625rem 0.875rem);
    font: inherit;
    color: var(--wa-color, inherit);
    background: var(--wa-bg, #fff);
    border: 1.5px solid var(--wa-border, oklch(80% 0.02 260));
    border-radius: var(--wa-radius, 10px);
    cursor: pointer;
    text-align: left;
    user-select: none;
    -webkit-appearance: none;
    appearance: none;
    box-shadow: 0 1px 3px oklch(0% 0 0 / 8%);
    transition: border-color 160ms ease, box-shadow 160ms ease;
  }

  .trigger:hover {
    border-color: var(--wa-border-hover, oklch(55% 0.22 270));
  }

  /* Double-ring focus indicator — contrasts on both light and dark backgrounds.
     Overridable from document CSS via ::part(trigger-focused). */
  .trigger:focus-visible,
  :host([open]) .trigger {
    outline: none;
    border-color: var(--wa-border-focus, oklch(55% 0.22 270));
    box-shadow:
      0 0 0 2px var(--wa-focus-inner, #fff),
      0 0 0 4px var(--wa-border-focus, oklch(55% 0.22 270));
  }

  .label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .label.is-placeholder {
    color: var(--wa-placeholder-color, oklch(60% 0.02 260));
  }

  /* Visually hidden — read by AT via aria-labelledby, not shown on screen */
  .label-ref {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  /* ── Chevron ── */
  .chevron {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    color: var(--wa-arrow-color, oklch(55% 0.22 270));
    transition: rotate 160ms ease;
  }

  :host([open]) .chevron { rotate: 180deg; }

  /* ── Listbox ── */
  .listbox {
    position: absolute;
    inset-inline-start: 0;
    top: calc(100% + 4px);
    min-width: 100%;
    margin: 0;
    padding: 5px;
    list-style: none;
    background: var(--wa-menu-bg, #fff);
    border: 1.5px solid var(--wa-menu-border, oklch(80% 0.02 260));
    border-radius: var(--wa-radius, 10px);
    box-shadow: 0 8px 24px oklch(0% 0 0 / 12%), 0 2px 6px oklch(0% 0 0 / 8%);
    max-height: 280px;
    overflow-y: auto;
    z-index: 1000;
    visibility: hidden;
    opacity: 0;
    translate: 0 -4px;
    transition: opacity 160ms ease, translate 160ms ease, visibility 0s 160ms;
  }

  :host([open]) .listbox {
    visibility: visible;
    opacity: 1;
    translate: 0 0;
    transition: opacity 160ms ease, translate 160ms ease;
  }

  /* ── Options ── */
  .option {
    padding: 0.5rem 0.75rem;
    border-radius: calc(var(--wa-radius, 10px) - 4px);
    cursor: pointer;
    font-size: inherit;
    color: var(--wa-menu-color, inherit);
    white-space: nowrap;
    transition: background 80ms ease;
  }

  .option.highlighted,
  .option:hover {
    background: var(--wa-option-hover, oklch(90% 0.06 270));
  }

  .option[aria-selected="true"] {
    background: var(--wa-option-selected-bg, oklch(55% 0.22 270));
    color: var(--wa-option-selected-color, #fff);
    font-weight: 500;
  }

  .option[aria-disabled="true"] {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }

  /* ── Group headers ── */
  .group-label {
    padding: 0.5rem 0.75rem 0.2rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--wa-group-color, oklch(55% 0.22 270));
    cursor: default;
    user-select: none;
  }

  .group-label:not(:first-child) {
    margin-block-start: 0.25rem;
    padding-block-start: 0.625rem;
    border-block-start: 1px solid var(--wa-group-divider, oklch(90% 0.02 260));
  }

  /* ── Reduced motion ── */
  @media (prefers-reduced-motion: reduce) {
    .trigger, .chevron, .listbox, .option {
      transition: none;
    }
  }

  /* ── Forced colors (Windows High Contrast, etc.) ── */
  @media (forced-colors: active) {
    .trigger {
      border-color: ButtonText;
      background: ButtonFace;
      color: ButtonText;
      forced-color-adjust: none;
    }
    .trigger:focus-visible,
    :host([open]) .trigger {
      outline: 2px solid Highlight;
      box-shadow: none;
    }
    .listbox {
      border-color: ButtonText;
      background: ButtonFace;
      forced-color-adjust: none;
    }
    .option[aria-selected="true"] {
      background: Highlight;
      color: HighlightText;
      forced-color-adjust: none;
    }
    .option.highlighted {
      outline: 1px solid Highlight;
      background: transparent;
    }
    .chevron {
      forced-color-adjust: auto;
    }
  }
</style>

<!-- label-ref is populated by #syncTriggerLabel() from an associated <label for> -->
<span class="label-ref" id="${labelRefId}"></span>

<button
  type="button"
  class="trigger"
  part="trigger"
  aria-haspopup="listbox"
  aria-expanded="false"
  aria-controls="${listboxId}"
  aria-labelledby="${labelRefId} ${labelId}"
>
  <span id="${labelId}" class="label is-placeholder" part="label"></span>
  <svg class="chevron" part="chevron" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path d="M2 4 L6 8 L10 4"
      fill="none" stroke="currentColor"
      stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
</button>

<ul
  class="listbox"
  part="listbox"
  role="listbox"
  id="${listboxId}"
  tabindex="-1"
></ul>`;
  }
}

if (!customElements.get('wa-select')) {
  customElements.define('wa-select', WaSelect);
}
